import { FEATURE_FLAGS } from '@/constants';
import { getMsalInstance } from './msal';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

const MANAGED_USER_IMAGE_SCOPES = ['ProfilePhoto.Read.All'];
const POPUP_FALLBACK_SCOPES = ['User.Read.All'];

const isConditionalAccessBlockedError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();
  return (
    message.includes('aadsts53003') ||
    message.includes('conditional access policies') ||
    message.includes('access has been blocked')
  );
};

/**
 * Fetches a managed user's profile photo from Microsoft Graph and returns a browser-safe object URL.
 *
 * High-level flow:
 * 1) Exit early when the feature flag is disabled.
 * 2) Request a Graph token silently through MSAL.
 * 3) Call the Graph photo endpoint for the provided `userId`.
 * 4) Convert the binary image response to a `Blob`.
 * 5) Create and return an object URL via `URL.createObjectURL(blob)`.
 *
 * Error handling behavior:
 * - Any failure returns `null`.
 * - If MSAL indicates interactive authentication is required
 *   (`InteractionRequiredAuthError`), this function triggers a popup token flow.
 *
 * Notes:
 * - This function intentionally returns `null` instead of throwing, so consuming UI
 *   can gracefully fall back to initials/placeholders.
 * - The object URL lifecycle is managed by the caller/context. If retained for long
 *   periods, consider revoking with `URL.revokeObjectURL(...)` when no longer needed.
 *
 * @param userId Azure AD user identifier for the managed user.
 * @returns Object URL string for the fetched image, or `null` when unavailable.
 */
export async function fetchUserMiscrosoftImage(userId: string): Promise<string | null> {
  // Guard clause: when the feature is disabled, skip all auth/network work.
  // This keeps behavior predictable across environments and rollout stages.
  if (!FEATURE_FLAGS.ENABLE_MANAGED_USER_PROFILE_IMAGE) {
    return null;
  }

  try {
    // ==================================================
    // Step 1: Acquire a Microsoft Graph access token
    // ==================================================
    // `getMsalInstance()` returns the shared MSAL singleton used app-wide.
    const msalInstance = getMsalInstance();

    // Scope needed for reading user profile photos in directory contexts.
    // (For signed-in user's own photo, other scope combinations may also work.)
    const scopes = MANAGED_USER_IMAGE_SCOPES;

    // The implementation chooses the first available account from MSAL.
    // This matches current app assumptions for a single active account context.
    const accounts = msalInstance.getAllAccounts();
    const account = accounts[0];

    if (!account) {
      console.warn('No active account found for managed-user photo fetch.');
      return null;
    }

    // Attempt silent token acquisition to avoid prompting the user.
    // If this fails due to consent/session requirements, the catch block handles it.
    const response = await msalInstance.acquireTokenSilent({ account, scopes });
    const accessToken = response.accessToken;

    // Continue only when an access token exists.
    if (response.accessToken) {
      // ==================================================
      // Step 2: Request the user's photo from Graph API
      // ==================================================
      // `/photo/$value` returns the raw image bytes (not JSON metadata).
      const graphUrl = `https://graph.microsoft.com/v1.0/users/${userId}/photo/$value`;

      // Build authorization and request headers expected by this endpoint.
      const bearerToken = `Bearer ${accessToken}`;
      const acceptHeader = 'image/*';
      const headers = { Authorization: bearerToken, Accept: acceptHeader };

      // Execute HTTP request to Graph.
      const imageResponse = await fetch(graphUrl, { headers });

      // Only process successful responses.
      if (imageResponse.ok) {
        // Convert binary stream -> Blob -> object URL for image rendering in the UI.
        const blob = await imageResponse.blob();
        const imageUrl = URL.createObjectURL(blob);

        // Success path: return image URL for immediate use in <img src="..." />.
        return imageUrl;
      }
    }
  } catch (error) {
    // Centralized logging for observability during auth/network failures.
    console.error('Error fetching Microsoft Graph user image:', error);

    // Special-case: interactive auth required (e.g., missing consent/session).
    // We trigger popup token acquisition to let the user complete interaction.
    if (error instanceof InteractionRequiredAuthError) {
      if (isConditionalAccessBlockedError(error)) {
        console.warn(
          'Managed-user photo fetch blocked by Conditional Access policy (AADSTS53003).',
        );
        return null;
      }

      // Call acquireTokenPopup in case of acquireTokenSilent failure when
      // policy permits interactive auth.
      try {
        await getMsalInstance().acquireTokenPopup({
          scopes: POPUP_FALLBACK_SCOPES,
        });
      } catch (popupError) {
        console.error('Error during popup token acquisition:', popupError);
      }
    }

    // Failure path: caller receives null and can render fallback avatar UX.
    return null;
  }

  // Default null return when no token or non-OK HTTP response was received.
  return null;
}
